<template>
    <div class="forms-page view-submission-page">
        <!-- Modern Header -->
        <div class="forms-header">
            <div class="header-left">
                <h1 class="page-title">
                    {{ formName || 'Loading...' }}
                    <span v-if="submissionLanguage && submissionLanguage !== 'en'" class="language-badge language-badge-es" :title="`Submitted in ${submissionLanguage.toUpperCase()}`">{{ submissionLanguage.toUpperCase() }}</span>
                </h1>
                <p class="page-subtitle">Form Submission Details</p>
            </div>
        </div>

        <ErrorDisplay/>

        <!-- Form Content Card -->
        <div class="submission-card" v-if="formData">
            <div id="export" class="submission-content">
                <FormRenderer
                    v-model="formInputData"
                    :form-configuration="formData"
                    :readOnly="readOnly"
                    :siteID="this.siteID"
                    :patNum="this.patNum"
                    :viewSubmissionMode="viewSubmissionMode"
                    :formSubmissionIDForWeb="formSubmissionIDForWeb"
                    :language="submissionLanguage"
                />
            </div>
        </div>

        <!-- Loading State - only show if not already showing global loading spinner -->
        <div v-else-if="!loading" class="loading-card">
            <div class="loading-state">
                <i class="fa fa-spinner fa-spin"></i>
                <span>Loading submission...</span>
            </div>
        </div>
    </div>
</template>

<script>
    /**
     * NOTE - This file only exists for development purpose.
     */
    import Vue from 'vue';
    import FormBuilder from "./FormBuilder";
    import FormRenderer from "./FormRenderer";    
    import { mapState, mapActions, mapMutations } from 'vuex';
    import ErrorDisplay from "@/components/ErrorDisplay/ErrorDisplay";
    import Multiselect from "vue-multiselect";
    import mixin from "../../../mixin.js";
    import "../css/bootstrap-4.3.1.min.css";
    import "../css/v-form-builder.css";
    import axios from 'axios';
    import _ from 'lodash';
    import { collectFormAttachments } from '../libraries/collect-attachments';
    import { isCategorizedDiseases, getOtherNote } from '../libraries/od-categorized-diseases';

    export default {
        name: "ViewSubmission",
        props: ['formSubmissionID'],
        mixins: [mixin],
        components: {
            FormBuilder,
            FormRenderer,
            ErrorDisplay,
            Multiselect
        },

        created() {            
            this.init();
            this.$formEvent.$on("submit", async(value) => {
                console.dir(this.formInputData);
                await this._saveFormSubmission();
            })
        },

        data: () => ({
            forms: [],
            selectedForm: [],
            formSubmissionData: [],
            patientNum: null,
            formName: "",
            formData: null,
            isShowDevNote: false,
            isRenderer: false,
            formInputData: null,
            isShowData: true,
            readOnly: false,
            viewSubmissionMode: true,   // whether we are viewing the submitted form so that i can hide certain things like page breaks, signature clear link etc.
            submissionLanguage: 'en',   // language the submission was made in
        }),

        methods: {
            ...mapActions('alert', ['setDismissSuccessCountDown', 'setErrored', 'setMessage']),
            ...mapActions('forms', ['getFormSubmission', 'saveFormSubmission']),

            async init() {
                if(!this.formSubmissionID) {                    
                    this.$toast.error('Form Submission ID is required. Please try again');
                    return false;
                } else {
                    let res = await this.getFormSubmission({formSubmissionIDForWeb: this.formSubmissionID});
                    console.log('res of getFormSubmission', res);
                    if(res.data) {
                        this.formName = res.data.Name;
                        this.formSubmissionIDForWeb = res.data.FormSubmissionIDForWeb;
                        this.formIDForWeb = res.data.FormIDForWeb;
                        this.formType = res.data.FormType;
                        this.patientNum = res.data.PatNum;
                        // Get the language the submission was made in (default to 'en' for backward compatibility)
                        this.submissionLanguage = res.data.Language || 'en';
                        this.formData = Object.assign({}, JSON.parse(res.data.ConfigData), {showSubmit: true});
                        //console.dir(this.formData);
                        try {
                            console.log('res.data.FormSubmissionData (raw, first 200 chars):', String(res.data.FormSubmissionData).substring(0,200));
                            console.log('Type of res.data.FormSubmissionData:', typeof res.data.FormSubmissionData);

                            let potentialObject = JSON.parse(res.data.FormSubmissionData);
                            let finalObject;

                            if (typeof potentialObject === 'string') {
                                console.log('First parse resulted in a string, attempting second parse (doubly encoded)...');
                                finalObject = JSON.parse(potentialObject);
                            } else {
                                console.log('First parse resulted in an object (singly encoded).');
                                finalObject = potentialObject;
                            }

                            console.log('After final processing, object type:', typeof finalObject);
                            // console.log('Final object value:', finalObject); // Potentially very large

                            this.formSubmissionData = finalObject;

                            Vue.nextTick(() => {                            
                                this.formInputData = finalObject; 
                            });    
                        } catch (error) {
                            console.log(error);
                            this.$toast.error('There was an issue parsing form data. Please contact teamio support');
                            return false;
                        }                        
                    }
                    else {
                        console.log(`Something went wrong with getting this form submission: ${res}`);
                    }
                }
            },

            formsCustomLabel({Name, FormIDForWeb}) {
                return `${Name}`;
            },

            viewRenderer() {
                if (!this.isRenderer) {
                    this.setData();
                    this.isRenderer = true;
                    return;
                }

                this.isRenderer = false;
            },

            getControlFromTeamioODColumn(teamioODColumn) {
                return _.findKey(this.formData.controls, function(o) {
                    return o.teamioODColumn === teamioODColumn;
                });
            },

            /**
             * A "DISEASES with Category and Checkbox Options" field asks for "Other" itself and keeps the
             * description on its "Other" item (libraries/od-categorized-diseases.js). The Diseases list
             * write stores that note only when it INSERTs or re-activates "Other"; for an "Other" that is
             * already active, forms-service updates the note ONLY from Other Problems, so the note is sent
             * as Other Problems too. (Until 2026-09-18 forms-service also RESOLVED "Other" whenever Other
             * Problems arrived blank; that branch is gone — see forms-service PROJECT_NOTES.) A legacy field
             * mapped to Other Problems (refused by the builder since 2026-09-18) keeps priority.
             * MIRRORED in ViewForm.vue (forms-od-categorized-other-note.test.js).
             *
             * @param {Object} odDataToSave - built by the submit loop; mutated in place
             */
            applyCategorizedOtherNote(odDataToSave) {
                if (this.getControlFromTeamioODColumn('Other Problems') || odDataToSave['Other Problems'] !== undefined) return;
                const key = _.findKey(this.formData.controls, c => !!c && c.teamioODColumn === 'Diseases' && isCategorizedDiseases(c));
                if (!key) return;
                const note = getOtherNote(this.formInputData[this.formData.controls[key].name]);
                if (note !== '') odDataToSave['Other Problems'] = note;
            },

            /*
                If you change the _saveFormSubmission method below then make sure to also change this in
                ViewForm.vue file as well
            */


            async _saveFormSubmission() {
                console.log('formInputData', JSON.stringify(this.formInputData, null, 2));
                
                let odDataToSave = {}
                for (const key of Object.keys(this.formData.controls)) {
                    let temp = this.formData.controls[key];                    
                    if(temp.teamioODColumn && temp.teamioODColumn !== '')  {    
                        //console.log('now looking at ', temp.teamioODColumn);
                        /*                        
                        console.log('so far temp', temp);
                        console.log('so far odDataToSave', odDataToSave); 
                        */

                        /*
                            There's a possibility that one or more fields can map to 1 OD filed. For example, former dentist and his phone number can BOTH map to 1 OD column called "Service"
                            So what I'm doing below is checking if there's already a value there and then appending existing value with a new line in between
                        */
                        
                        if(temp.teamioODColumn === 'Medical' || temp.teamioODColumn === 'Service') {                            
                            if(Array.isArray(this.formInputData[temp.name]))
                            {
                                if(this.formInputData[temp.name].length !== 0) {
                                    let valToSave = `${temp.label}: ${this.formInputData[temp.name]}`;
                                    odDataToSave[temp.teamioODColumn] = odDataToSave[temp.teamioODColumn] ? odDataToSave[temp.teamioODColumn] + '\n' + valToSave : valToSave;
                                }
                            } else {
                                if(this.formInputData[temp.name] !== '') {
                                    let valToSave = `${temp.label}: ${this.formInputData[temp.name]}`;
                                    odDataToSave[temp.teamioODColumn] = odDataToSave[temp.teamioODColumn] ? odDataToSave[temp.teamioODColumn] + '\n' + valToSave : valToSave;
                                }
                            }
                        } 
                        /*
                            Here I'm taking care of the case where the user might just put something in other allergies, other problems, etc...WITHOUT actually selecting "other" option in 
                            allergy, medications or problems dropdown. So i'm doing that automatically here
                        */
                        else if(temp.teamioODColumn === 'Other Allergies' || temp.teamioODColumn === 'Other Problems' || temp.teamioODColumn === 'Other Medications') {                                                              
                            let ctrlKey;
                            let currentValue = this.formInputData[temp.name];
                            if(currentValue.trim() !== '') {
                                console.log('found other column', temp.name);
                                console.log('current value', currentValue);                                    

                                switch (temp.teamioODColumn) {
                                    case 'Other Allergies': // let's find the allergy field first and add "Other" as an option if it's not already there
                                                            ctrlKey = this.getControlFromTeamioODColumn('Allergies');                                                                
                                                            break;

                                    case 'Other Problems': 
                                                            ctrlKey = this.getControlFromTeamioODColumn('Diseases');                                                                
                                                            break;

                                    case 'Other Medications': 
                                                            ctrlKey = this.getControlFromTeamioODColumn('Medications');                                                                
                                                            break;                                                                

                                    default:                break;
                                }

                                console.log('ctrlKey', ctrlKey);
                                let ctrl = this.formData.controls[ctrlKey];
                                // let's make sure other is there already. if not, then add it
                                let currentSelectedOptions = this.formInputData[ctrl.name];
                                console.log('currentSelectedOptions', JSON.stringify(currentSelectedOptions, null, 2));
                                let otherOption = currentSelectedOptions.find(a => a.text.toUpperCase() === 'OTHER');
                                if(otherOption) {                                    
                                    console.log('other is already selected');
                                    console.log('otherOption', otherOption);
                                    otherOption['otherTypeDescriptionText'] = currentValue;
                                } else {
                                    // find the option to push
                                    let optionToPush = ctrl.selectedOptions.find(a => a.text.toUpperCase() === 'OTHER');
                                    console.log('found other option', optionToPush);
                                    console.log('now adding it to', this.formInputData[ctrl.name]);

                                    /*
                                        Dropdown only returns 2 things. Text and value. But we need 3 things on backend. text, value and also reaction if it's other...so we are going to add a 3rd property to the dropdown
                                        selected option called otherTypeDescriptionText
                                        otherTypeDescriptionText variable is what we're using to store the description of the text that the patient may type into
                                        "Other Allergy", "Other Medication", "Other Problems/Diseases" Textbox.
                                    */

                                    if(optionToPush) {
                                        optionToPush['otherTypeDescriptionText'] = currentValue;
                                        this.formInputData[ctrl.name].push(optionToPush);                                                                    
                                        console.log('other option added automatically');
                                    }
                                }
                                odDataToSave[temp.teamioODColumn] = odDataToSave[temp.teamioODColumn] ? odDataToSave[temp.teamioODColumn] + '\n' + this.formInputData[temp.name] : this.formInputData[temp.name];
                            }
                        }
                        else                        
                            odDataToSave[temp.teamioODColumn] = odDataToSave[temp.teamioODColumn] ? odDataToSave[temp.teamioODColumn] + '\n' + this.formInputData[temp.name] : this.formInputData[temp.name];
                    }
                }
                
                this.applyCategorizedOtherNote(odDataToSave);

                console.log('odDataToSave', JSON.stringify(odDataToSave, null, 2));

                // let's find any controls that are of type "fileUploader"
                
                const { attachments, error: attachmentError } = collectFormAttachments(this.formData.controls, this.formInputData);
                if (attachmentError) {
                    this.$toast.error(attachmentError);
                    return false;
                }

                let payload = {
                    formName: this.formName,
                    formIDForWeb: this.formIDForWeb,
                    formSubmissionIDForWeb: this.formSubmissionIDForWeb,
                    formSubmissionData: JSON.stringify(this.formInputData),
                    formType: this.formType,
                    odDataToSave: odDataToSave,
                    siteID: this.siteID,
                    patNum: this.patientNum,                    
                    userODNum: this.user && this.user.ODUserNum ? this.user.ODUserNum : 0,
                    patientName: (odDataToSave.FName && odDataToSave.LName) ? `${odDataToSave.FName} ${odDataToSave.LName}` : (odDataToSave.FName || odDataToSave.LName || null),
                    pdfCategory: this.formData.formConfig.pdfCategory,
                    pdfFileName: this.formData.formConfig.pdfFileName,
                    attachmentCategory: this.formData.formConfig.attachmentCategory,
                    attachments: attachments,
                    submittedByPatient: false,
                    submittedByStaffName: this.user && this.user.Name ? this.user.Name : ''
                }
                //console.dir(this.formInputData);
                //console.log("Now submitting following data");                
                //console.log('payload', JSON.stringify(payload, null, 2));
                console.log('payload', payload);

                try {
                    let res = await this.saveFormSubmission(payload);
                    //console.log(res);
                    if(res.status === 200) {
                        let siteInfo = res.data;
                        this.$router.push({
                            name: 'FormResult',
                            params: {                            
                                logoUrl: this.formData.formConfig.logoUrl,
                                siteInfo: siteInfo
                            }                        
                        });
                    }
                } catch(error) {
                    console.log(error);                                
                    this.$toast.error(`Something went wrong. Please try again later`);
                }                
            }
        },

        computed: {            
            ...mapState({
                loading: state => state.alert.loading,
                loaded: state => state.alert.loaded,
                errored: state => state.alert.errored,
                alertMessage: state => state.alert.message,                
            })
        }
    }
</script>

<!-- New step!
     Add Multiselect CSS. Can be added as a static asset or inside a component. -->
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss" scoped>
// Modern Forms Page Styling - matching Admin Dashboard
.forms-page.view-submission-page {
    font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
    color: #334155;
    background-color: #f8fafc;
    padding: 1.5rem 2rem;
    min-height: 100vh;

    @media (max-width: 768px) {
        padding: 1rem;
    }
}

// Header
.forms-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;

    .header-left {
        flex: 1;
    }

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
    }
}

.page-title {
    font-size: 1.75rem;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 4px 0;
    line-height: 1.3;
    display: flex;
    align-items: center;
    gap: 12px;
}

// Language badge for submissions
.language-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.language-badge-es {
    background-color: #fef3c7;
    color: #92400e;
    border: 1px solid #fcd34d;
}

.page-subtitle {
    font-size: 0.9375rem;
    color: #64748b;
    margin: 0;
}

// Submission Card
.submission-card {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;
    overflow: hidden;
}

.submission-content {
    padding: 32px 40px;

    @media (max-width: 768px) {
        padding: 20px;
    }
}

// Loading Card
.loading-card {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;
}

// Loading State
.loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 80px 20px;
    color: #64748b;
    font-size: 0.9375rem;

    i {
        font-size: 1.5rem;
        color: #3abf94;
    }
}

// Deep styles for FormRenderer inside submission
::v-deep .vue-form-renderer {
    // Section styling
    .normal-section {
        margin-bottom: 24px;
        padding: 0;
    }

    // Form group styling
    .form-group {
        margin-bottom: 20px;
    }

    // Labels
    label {
        font-weight: 600;
        font-size: 0.8125rem;
        color: #475569;
        margin-bottom: 6px;
        display: block;
    }

    // Input styling
    input[type="text"],
    input[type="email"],
    input[type="tel"],
    input[type="number"],
    input[type="date"],
    select,
    textarea {
        width: 100%;
        padding: 10px 14px;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        font-size: 0.875rem;
        color: #334155;
        background-color: white;
        transition: all 0.2s ease;
        font-family: inherit;

        &:focus {
            outline: none;
            border-color: #3abf94;
            box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.1);
        }

        &:disabled,
        &[readonly] {
            background-color: #f8fafc;
            color: #64748b;
            cursor: not-allowed;
        }
    }

    // Checkbox and radio styling
    input[type="checkbox"],
    input[type="radio"] {
        width: 18px;
        height: 18px;
        accent-color: #3abf94;
        margin-right: 8px;
    }

    // Section headlines
    .headline-block {
        h1, h2, h3, h4, h5, h6 {
            font-weight: 600;
            color: #0f172a;
            margin-bottom: 8px;
        }

        p {
            color: #64748b;
            font-size: 0.9375rem;
        }
    }

    // Control list container
    .control-list-container {
        margin: 0;
        padding: 0;
    }

    // Hide submit button in view mode
    .btn-submit,
    button[type="submit"] {
        display: none;
    }

    // Table styling for table sections
    table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 16px;

        th, td {
            padding: 12px;
            text-align: left;
            border-bottom: 1px solid #e2e8f0;
        }

        th {
            font-weight: 600;
            color: #475569;
            background-color: #f8fafc;
        }
    }
}
</style>
